import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ArrowLeft, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import GuestPortalFooter from "../components/GuestPortalFooter";
import ConciergePackagesSection from "../components/ConciergePackagesSection";
import { setAgentThemeColor } from "../utils/agentTheme";
import { StepProgressIndicator } from "../components/StepProgressIndicator";
import { API, BACKEND_URL } from "../config/api";


// Helper to resolve image URLs (handles both absolute URLs and relative paths)
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
  if (url.startsWith('/api/uploads/')) return `${BACKEND_URL}${url}`;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Sort order for upgrade services (used when filtering)
const getUpgradeOrder = (serviceName) => {
  const name = serviceName?.toLowerCase() || '';
  if (name.includes('reservation')) return 1;
  if (name.includes('signature') || name.includes('planning')) return 2;
  if (name.includes('on-demand') || name.includes('on demand')) return 3;
  if (name.includes('vip')) return 4;
  return 99;
};

// Get a short description for card preview (first line, truncated)
const getShortDescription = (description, maxLength = 120) => {
  if (!description) return '';
  
  // Get first line or first chunk of text
  const firstLine = description.split('\n')[0] || description;
  const cleaned = firstLine.trim();
  
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.substring(0, maxLength).trim() + '...';
};

// Default fallback config (used until CMS content loads)
const defaultCategoryConfig = {
  pre_arrival: {
    label: "Pre-Arrival",
    title: "Pre-Arrival Services",
    intro: "Pre-arrival concierge is included in your booking, and we are delighted to assist with arranging the following services ahead of your stay:",
    type: "services"
  },
  on_island: {
    label: "In Destination",
    title: "In Destination Services",
    intro: "Enhance your time at your destination with our curated selection of wellness and lifestyle services.",
    type: "services"
  },
  private_chefs: {
    label: "Private Chefs",
    title: "Private Chefs",
    intro: "Elevate your dining experience with our selection of talented private chefs, offering bespoke culinary experiences in the comfort of your villa.",
    type: "services"
  },
  yacht_charter: {
    label: "Yacht Charter",
    title: "Yacht Charter",
    intro: "Explore the stunning coastline aboard a luxury yacht. Choose from our collection of motor and sailing yachts.",
    type: "yachts"
  },
  experiences: {
    label: "Experiences",
    title: "Experiences",
    intro: "Discover curated experiences that bring the best of the island to you.",
    type: "experiences"
  },
  upgrades: {
    label: "Service Level",
    title: "Choose how you'd like your stay to be managed",
    intro: "Enhance your stay with our concierge support options. Our team can manage reservations, bookings and last-minute requests so you can simply enjoy your time on the island.",
    type: "services"
  }
};

export default function ServicesPage() {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [agentContext, setAgentContext] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selections, setSelections] = useState([]);
  
  // Initialize activeCategory from sessionStorage or default to "pre_arrival"
  const [activeCategory, setActiveCategory] = useState(() => {
    const savedTab = sessionStorage.getItem("activeServiceTab");
    return savedTab || "pre_arrival";
  });
  
  // CMS Page Content
  const [pageContent, setPageContent] = useState(null);
  const [categoryConfig, setCategoryConfig] = useState(defaultCategoryConfig);
  
  // Dynamic categories from database
  const [experienceCategories, setExperienceCategories] = useState([]);
  const [yachtCategories, setYachtCategories] = useState([]);
  const [destinationConfig, setDestinationConfig] = useState(null); // Settings for current destination
  const [allExperiences, setAllExperiences] = useState([]); // For destinations without categories

  // Save active tab to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem("activeServiceTab", activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    const loadData = async () => {
      const bookingData = sessionStorage.getItem("bookingData");
      if (!bookingData) {
        navigate("/");
        return;
      }

      const parsed = JSON.parse(bookingData);
      setBooking(parsed);
      setSelections(parsed.selections || []);
      
      // Load agent context if available
      const agentData = sessionStorage.getItem("agentContext");
      if (agentData) {
        const parsedAgent = JSON.parse(agentData);
        setAgentContext(parsedAgent);
        // Set agent theme color for all buttons
        setAgentThemeColor(parsedAgent.button_color || parsed.button_color);
      } else {
        // Fallback to booking data for button color
        setAgentThemeColor(parsed.button_color);
      }

      try {
        // Load services, page content, and categories in parallel
        const [servicesRes, contentRes, expCatsRes, yachtCatsRes, destConfigRes] = await Promise.all([
          axios.get(`${API}/services`, { params: { destination: parsed.destination } }),
          axios.get(`${API}/page-content/services`).catch(() => ({ data: null })),
          axios.get(`${API}/experiences/categories`, { params: { destination: parsed.destination } }).catch(() => ({ data: [] })),
          axios.get(`${API}/yachts/categories`, { params: { destination: parsed.destination } }).catch(() => ({ data: [] })),
          axios.get(`${API}/destinations/${parsed.destination}`).catch(() => ({ data: null }))
        ]);
        
        setServices(servicesRes.data);
        setExperienceCategories(expCatsRes.data || []);
        setYachtCategories(yachtCatsRes.data || []);
        
        // Set destination config (for use_experience_categories setting)
        if (destConfigRes.data) {
          setDestinationConfig(destConfigRes.data);
        }
        
        // Check if this destination has experience-services (experiences_courchevel, etc.)
        const destSlug = parsed.destination;
        const experienceServices = servicesRes.data.filter(
          s => s.category === `experiences_${destSlug}` && s.status === 'Active'
        );
        
        if (experienceServices.length > 0) {
          // Destination has experience-services — show them as flat list
          setAllExperiences(experienceServices.map(s => ({
            ...s,
            hero_image: s.image_url,
            short_description: s.description || s.short_description || '',
          })));
          setDestinationConfig(prev => ({ ...prev, use_experience_categories: false }));
        } else if (destConfigRes.data?.use_experience_categories === false) {
          // Destination uses flat experience list from experiences collection
          try {
            const expRes = await axios.get(`${API}/experiences`, { params: { destination: parsed.destination } });
            const expData = expRes.data;
            setAllExperiences(Array.isArray(expData) ? expData : expData.experiences || []);
          } catch (e) {
            console.error("Failed to load experiences:", e);
          }
        }
        
        // Apply CMS content if available
        if (contentRes.data) {
          setPageContent(contentRes.data);
          
          // Build categoryConfig from CMS sections
          const sections = contentRes.data.sections || [];
          const newConfig = { ...defaultCategoryConfig };
          
          sections.forEach(section => {
            const sectionId = section.id || section.key;
            if (newConfig[sectionId]) {
              newConfig[sectionId] = {
                ...newConfig[sectionId],
                title: section.title || newConfig[sectionId].title,
                intro: section.intro || newConfig[sectionId].intro
              };
            }
          });
          
          setCategoryConfig(newConfig);
        }
      } catch (error) {
        toast.error("Failed to load services");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [navigate]);

  const getOrderedServices = (category) => {
    // Yacht Charter and Experiences are handled as separate tabs, not service items
    if (category === 'yacht_charter' || category === 'experiences') {
      return [];
    }
    
    // Filter services by category (check both primary category and categories array)
    // This allows a single service to appear in multiple sections
    const categoryServices = services.filter(s => {
      const inPrimaryCategory = s.category === category;
      const inCategoriesArray = s.categories?.includes(category);
      const isUpgrade = category === 'upgrades';
      return (inPrimaryCategory || inCategoriesArray) && 
        (s.status === 'Active' || isUpgrade) &&
        s.name !== "Yacht Charter" && 
        s.name !== "Experiences";
    });
    
    // For upgrades, use fixed order: Restaurant > On-Demand > VIP
    if (category === 'upgrades') {
      return categoryServices.sort((a, b) => getUpgradeOrder(a.name) - getUpgradeOrder(b.name));
    }
    
    // Sort by database order field (ascending) - this is the only source of truth
    return categoryServices.sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Retry helper for API calls during startup transients
  const apiWithRetry = async (fn, maxRetries = 3, delayMs = 2000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        const status = error.response?.status;
        const isRetryable = !status || status >= 500 || status === 0;
        if (attempt < maxRetries && isRetryable) {
          await new Promise(r => setTimeout(r, delayMs));
        } else {
          throw error;
        }
      }
    }
  };

  const handleServiceClick = (service) => {
    // For ALL upgrades (check both category and categories array), directly toggle selection (no modal)
    if (service.category === 'upgrades' || service.categories?.includes('upgrades')) {
      handleUpgradeToggle(service);
      return;
    }
    // For other services with questions, open modal
    setSelectedService(service);
    const existing = selections.find(s => s.service_id === service.id);
    setAnswers(existing?.answers || {});
  };

  // Direct toggle for upgrades without questions
  const handleUpgradeToggle = async (service) => {
    const existingSelection = selections.find(s => s.service_id === service.id);
    const isSelected = !!existingSelection;
    
    if (isSelected) {
      // Remove from plan
      try {
        const updatedSelections = selections.filter(s => s.service_id !== service.id);
        setSelections(updatedSelections);
        
        // Use the selection's id for the delete endpoint
        await apiWithRetry(() => axios.delete(`${API}/bookings/${booking.id}/selections/${existingSelection.id}`));
        
        const updatedBooking = { ...booking, selections: updatedSelections };
        setBooking(updatedBooking);
        sessionStorage.setItem("bookingData", JSON.stringify(updatedBooking));
        
        toast.success(`Removed from your plan`);
      } catch (error) {
        // Restore selection if API fails
        setSelections(selections);
        toast.error("Failed to remove");
      }
    } else {
      // Add to plan
      try {
        // First call API to get the server-generated selection ID
        const response = await apiWithRetry(() => axios.post(`${API}/bookings/${booking.id}/selections`, {
          service_id: service.id,
          service_name: service.name,
          image_url: service.image_url || '',
          answers: {}
        }));
        
        // Use the selection returned from server (has correct ID)
        const newSelection = response.data.selection;
        const updatedSelections = [...selections, newSelection];
        setSelections(updatedSelections);
        
        const updatedBooking = { ...booking, selections: updatedSelections };
        setBooking(updatedBooking);
        sessionStorage.setItem("bookingData", JSON.stringify(updatedBooking));
        
        toast.success(`${service.name} added to your plan`);
      } catch (error) {
        toast.error("Failed to add");
      }
    }
  };

  const handleYachtCategory = (categoryName) => {
    // Navigate directly to the yacht listing for this category
    navigate(`/yachts/${encodeURIComponent(categoryName)}`);
  };

  const handleExperienceCategory = (categorySlug) => {
    // Navigate directly to the experience listing for this category
    navigate(`/experiences/${categorySlug}`);
  };

  const handleAddToPlan = async () => {
    if (!selectedService) return;

    const missingRequired = selectedService.questions
      .filter(q => q.required && !answers[q.id])
      .map(q => q.label);

    if (missingRequired.length > 0) {
      toast.error(`Please complete: ${missingRequired.join(", ")}`);
      return;
    }

    setIsSaving(true);
    try {
      const existingIndex = selections.findIndex(s => s.service_id === selectedService.id);
      
      const newSelection = {
        id: crypto.randomUUID(),
        service_id: selectedService.id,
        service_name: selectedService.name,
        image_url: selectedService.image_url || '',
        answers: answers,
        added_at: new Date().toISOString()
      };

      let updatedSelections;
      if (existingIndex >= 0) {
        updatedSelections = [...selections];
        updatedSelections[existingIndex] = { ...updatedSelections[existingIndex], answers };
      } else {
        updatedSelections = [...selections, newSelection];
      }
      
      setSelections(updatedSelections);

      await apiWithRetry(() => axios.post(`${API}/bookings/${booking.id}/selections`, {
        service_id: selectedService.id,
        service_name: selectedService.name,
        image_url: selectedService.image_url || '',
        answers: answers
      }));

      // Close the dialog immediately on success
      setSelectedService(null);
      setAnswers({});

      const updatedBooking = { ...booking, selections: updatedSelections };
      setBooking(updatedBooking);
      sessionStorage.setItem("bookingData", JSON.stringify(updatedBooking));

      toast.success(`Added to your plan`);
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const isServiceSelected = (serviceId) => selections.some(s => s.service_id === serviceId);

  // Define the tab order for sequential navigation
  const TABS_ORDER = ["pre_arrival", "on_island", "private_chefs", "yacht_charter", "experiences", "upgrades"];
  
  // Get visible tabs in order (respecting yacht visibility and private chefs availability)
  const getVisibleTabsOrder = () => {
    return TABS_ORDER.filter(key => {
      if (key === 'yacht_charter' && !showYachts) return false;
      if (key === 'private_chefs' && !hasPrivateChefServices) return false;
      return true;
    });
  };

  const handleContinue = () => {
    const visibleTabs = getVisibleTabsOrder();
    const currentIndex = visibleTabs.indexOf(activeCategory);
    
    // If we're on the last tab (Upgrades), go to summary
    if (currentIndex === visibleTabs.length - 1) {
      const updatedBooking = { ...booking, selections };
      sessionStorage.setItem("bookingData", JSON.stringify(updatedBooking));
      navigate("/summary");
    } else {
      // Move to next tab
      const nextTab = visibleTabs[currentIndex + 1];
      setActiveCategory(nextTab);
      // Scroll to top smoothly
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    const visibleTabs = getVisibleTabsOrder();
    const currentIndex = visibleTabs.indexOf(activeCategory);
    
    // If we're on the first tab, go back to agent landing page
    if (currentIndex === 0) {
      navigate(getAgentLandingUrl());
    } else {
      // Move to previous tab
      const prevTab = visibleTabs[currentIndex - 1];
      setActiveCategory(prevTab);
      // Scroll to top smoothly
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Get the agent landing URL
  const getAgentLandingUrl = () => {
    const slug = agentContext?.agent_slug || booking?.agent_slug || "charles-marlow";
    return `/a/${slug}`;
  };
  
  // Check if we're on the first tab
  const isFirstTab = () => {
    const visibleTabs = getVisibleTabsOrder();
    return visibleTabs.indexOf(activeCategory) === 0;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const currentCategory = categoryConfig[activeCategory];
  const currentServices = getOrderedServices(activeCategory);
  const isUpgradeCategory = activeCategory === "upgrades";
  const isYachtSection = activeCategory === "yacht_charter";
  const isExperienceSection = activeCategory === "experiences";
  
  // Check agent type for custom wording
  const isYachtCharterCompany = agentContext?.agent_type === "yacht_charter_company" || booking?.agent_type === "yacht_charter_company";
  
  // Get dynamic intro text based on agent type, category, and destination
  const getCategoryIntro = () => {
    if (activeCategory === "pre_arrival" && isYachtCharterCompany) {
      return "Here is a selection of services to help organise your arrival. Already there? Simply click Continue to explore our In Destination Services.";
    }
    // Dynamic destination name for experiences intro
    if (activeCategory === "experiences") {
      const destName = destinationConfig?.name || booking?.destination?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "your destination";
      return `Discover curated experiences that bring the best of ${destName} to you.`;
    }
    return currentCategory?.intro || "";
  };
  
  // Check if yachts should be shown for this specific destination
  const getShowYachts = () => {
    const selectedDest = booking?.destination?.toLowerCase();
    
    // First check: Are there any yacht categories with yachts for this destination?
    // If no yachts exist at all, don't show the yacht section
    const hasYachtsForDestination = yachtCategories && yachtCategories.length > 0 && 
      yachtCategories.some(cat => cat.count > 0);
    
    if (!hasYachtsForDestination) {
      return false; // No yachts available for this destination - hide section completely
    }
    
    // Check per-destination setting from agent config
    const byDestSetting = agentContext?.show_yachts_by_destination?.[selectedDest];
    if (byDestSetting !== undefined) {
      return byDestSetting;
    }
    // Fall back to global show_yachts setting
    return booking?.show_yachts !== false && agentContext?.show_yachts !== false;
  };
  const showYachts = getShowYachts();
  
  // Check if water experiences should be shown
  const showWaterExperiences = booking?.show_water_experiences !== false && agentContext?.show_water_experiences !== false;
  
  // Check if there are private chef services
  const hasPrivateChefServices = services.some(s => 
    s.category === 'private_chefs' || s.categories?.includes('private_chefs')
  );
  
  // Filter categories based on agent settings and available services
  const visibleCategories = Object.entries(categoryConfig).filter(([key]) => {
    if (key === 'yacht_charter' && !showYachts) return false;
    if (key === 'private_chefs' && !hasPrivateChefServices) return false;
    return true;
  });
  
  // Filter experience categories based on agent settings
  const filteredExperienceCategories = experienceCategories.filter(cat => {
    // Hide "water" category if show_water_experiences is false
    if (!showWaterExperiences && (cat.slug === 'water' || cat.name?.toLowerCase() === 'water' || cat.name?.toLowerCase().includes('on the water'))) {
      return false;
    }
    return true;
  });
  
  // Get agent name and logo for branding
  const agentName = agentContext?.agent_name || booking?.agent_name || "Charles Marlow";
  
  // Get full logo URL
  const getLogoUrl = () => {
    const logoUrl = agentContext?.logo_url || booking?.logo_url;
    if (!logoUrl) return null;
    if (logoUrl.startsWith('/')) {
      return `${BACKEND_URL}${logoUrl}`;
    }
    return logoUrl;
  };

  return (
    <div className="min-h-screen bg-background pb-44">
      {/* Header with Logo */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm">
        <div className="container-luxury py-8 flex items-center justify-between">
          <button
            onClick={handleBack}
            data-testid="back-button"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          
          {/* Agent Logo or Name */}
          {getLogoUrl() ? (
            <img 
              src={getLogoUrl()} 
              alt={agentName}
              className="h-16 md:h-20 object-contain"
              style={{ maxWidth: '240px' }}
            />
          ) : (
            <span className="brand-logo brand-logo-small">{agentName}</span>
          )}
          
          <div className="w-4" />
        </div>

        {/* Progress indicator */}
        <div className="container-luxury pb-2">
          <StepProgressIndicator currentStep={2} />
        </div>

        {/* Category tabs - Wrapping layout, all buttons fully visible */}
        <div className="container-luxury pt-4 pb-5 border-b border-border/40">
          <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
            {visibleCategories.map(([key, config]) => (
              <button
                key={key}
                onClick={() => {
                  setActiveCategory(key);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                data-testid={`tab-${key}`}
                className={`whitespace-nowrap px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm md:text-base font-medium transition-all ${
                  activeCategory === key 
                    ? "bg-primary text-white" 
                    : "bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                {config.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content - pt must clear the fixed header (tabs wrap on mobile, making header taller) */}
      <div className="container-luxury pt-96 sm:pt-80">
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Main section title - for Upgrades this is "Enhance Your Stay" */}
          {!isUpgradeCategory && (
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif text-foreground mb-6 sm:mb-8 text-center">
              {currentCategory.title}
            </h1>
          )}
          
          {/* Intro paragraph - Hide for upgrades since it has its own header */}
          {!isUpgradeCategory && getCategoryIntro() && (
            <p className="text-sm sm:text-base text-muted-foreground mb-10 sm:mb-16 max-w-2xl mx-auto leading-relaxed text-center" style={{ textWrap: 'pretty' }}>
              {getCategoryIntro()}
            </p>
          )}

          {/* ==================== CONCIERGE PACKAGES SECTION ==================== */}
          {isUpgradeCategory && (
            <ConciergePackagesSection
              services={currentServices.filter(s => s.category === 'upgrades' || s.categories?.includes('upgrades'))}
              selectedServiceIds={selections.map(s => s.service_id)}
              onSelectService={handleServiceClick}
              onConfirm={handleContinue}
              destination={booking?.destination}
              agentSlug={booking?.agent_slug}
            />
          )}
          {/* Yacht Charter Section - Dynamic categories from CMS */}
          {isYachtSection && (
            <>
              <div className="grid gap-8 grid-cols-1 md:grid-cols-2">
                {(yachtCategories.length > 0 ? yachtCategories : [
                  { slug: "motor-yachts", name: "Motor Yachts", description: "Power and luxury combined for coastal exploration", image: "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=800&q=80" },
                  { slug: "sailing-yachts", name: "Sailing Yachts", description: "Classic elegance and the freedom of the wind", image: "https://images.unsplash.com/photo-1540946485063-a40da27545f8?w=800&q=80" }
                ]).map((category, index) => (
                  <motion.div
                    key={category.slug || category.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08, duration: 0.4 }}
                  >
                    <button
                      data-testid={`yacht-${category.slug || category.name?.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => handleYachtCategory(category.name)}
                      className="w-full text-left tile-service"
                    >
                      <div className="relative overflow-hidden">
                        <img
                          src={getImageUrl(category.image || category.image_url)}
                          alt={category.name}
                          className="tile-image"
                        />
                      </div>
                      <div className="tile-content">
                        <h3 className="tile-name">{category.name}</h3>
                        {category.price_display && (
                          <p className="tile-price">{category.price_display}</p>
                        )}
                        <p className="tile-benefit">{category.description}</p>
                        <div className="tile-button text-center">
                          View Collection
                        </div>
                      </div>
                    </button>
                  </motion.div>
                ))}
              </div>
            </>
          )}

          {/* Experiences Section - Dynamic categories from CMS */}
          {isExperienceSection && (
            <>
              {/* Check if destination uses categories or simple list */}
              {destinationConfig?.use_experience_categories === false ? (
                /* Simple list view for destinations without categories (e.g., Courchevel, Morocco) */
                <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {allExperiences.length > 0 ? (
                    allExperiences.map((experience, index) => (
                      <motion.div
                        key={experience.id || index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.08, duration: 0.4 }}
                      >
                        <button
                          data-testid={`experience-${experience.id}`}
                          onClick={() => experience.category?.startsWith('experiences_') ? handleServiceClick(experience) : navigate(`/experiences/${experience.id}`)}
                          className="w-full text-left tile-service"
                        >
                          <div className="relative overflow-hidden">
                            <img
                              src={getImageUrl(experience.hero_image || experience.image)}
                              alt={experience.name}
                              className="tile-image"
                            />
                          </div>
                          <div className="tile-content">
                            <h3 className="tile-name">{experience.name}</h3>
                            <p className="tile-benefit">{experience.short_description}</p>
                            {experience.from_price > 0 && (
                              <p className="text-sm text-primary font-medium mt-2">
                                From €{experience.from_price}
                              </p>
                            )}
                            <div className="tile-button text-center">
                              View Details
                            </div>
                          </div>
                        </button>
                      </motion.div>
                    ))
                  ) : (
                    <p className="col-span-full text-center text-muted-foreground py-8">
                      No experiences available for this destination yet.
                    </p>
                  )}
                </div>
              ) : (
                /* Category-based view for destinations with categories (e.g., Mallorca, Ibiza) */
                <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {(filteredExperienceCategories.length > 0 ? filteredExperienceCategories : [
                    { slug: "food", name: "Food", description: "Culinary experiences and private dining", image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80" },
                    { slug: "art", name: "Art", description: "Gallery tours and creative workshops", image: "https://images.unsplash.com/photo-1531913764164-f85c52e6e654?w=800&q=80" },
                    { slug: "tours", name: "Tours", description: "Guided explorations of the island", image: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&q=80" },
                    { slug: "wine", name: "Wine", description: "Vineyard visits and tastings", image: "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=800&q=80" },
                    { slug: "at-the-villa", name: "At the Villa", description: "In-home experiences and entertainment", image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80" }
                  ].filter(cat => showWaterExperiences || cat.slug !== 'water')).map((category, index) => (
                    <motion.div
                      key={category.slug || category.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.08, duration: 0.4 }}
                    >
                      <button
                        data-testid={`experience-${category.slug || category.id}`}
                        onClick={() => handleExperienceCategory(category.slug || category.id)}
                        className="w-full text-left tile-service"
                      >
                        <div className="relative overflow-hidden">
                          <img
                            src={getImageUrl(category.image || category.image_url)}
                            alt={category.name}
                            className="tile-image"
                          />
                        </div>
                        <div className="tile-content">
                          <h3 className="tile-name">{category.name}</h3>
                          <p className="tile-benefit">{category.description}</p>
                          <div className="tile-button text-center">
                            Explore
                          </div>
                        </div>
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Services grid - for Pre-Arrival and In Destination ONLY (Upgrades handled above) */}
          {!isYachtSection && !isExperienceSection && !isUpgradeCategory && (
            <>
              {/* Why Book Through Our Concierge Team - Trust Section (Pre-Arrival only) */}
              {activeCategory === "pre_arrival" && (
                <div className="mb-10 md:mb-12">
                  <div className="bg-secondary/30 rounded-xl p-5 md:p-6 border border-border/30">
                    <h3 className="text-sm font-medium text-foreground mb-4 tracking-wide">
                      Why Book Through Our Concierge Team
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-xs text-muted-foreground">Trusted local suppliers</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-xs text-muted-foreground">Over 10 years experience</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-xs text-muted-foreground">Full destination knowledge</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-xs text-muted-foreground">Personalised recommendations</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-primary" />
                        </div>
                        <span className="text-xs text-muted-foreground">Our team is on the ground</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {currentServices.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  {/* Regular Service Card */}
                  <button
                    data-testid={`service-${service.id}`}
                    onClick={() => handleServiceClick(service)}
                    className="w-full text-left tile-service"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={getImageUrl(service.image_url)}
                        alt={service.name}
                        className="tile-image"
                      />
                      {isServiceSelected(service.id) && (
                        <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                          <Check className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="tile-content">
                      <h3 className="tile-name">{service.name}</h3>
                      <p className="tile-price">{service.price_display}</p>
                      <p className="tile-benefit">{getShortDescription(service.description)}</p>
                      <div className="tile-button text-center">
                        {isServiceSelected(service.id) ? "✓ Added to plan" : "View Details →"}
                      </div>
                    </div>
                  </button>
                </motion.div>
              ))}
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Enhanced Fixed Bottom Bar with Selected Services */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-lg border-t border-border/40 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4">
          {/* Selected Items Preview - Only show when items exist */}
          {selections.length > 0 && (
            <div className="mb-3 pb-3 border-b border-border/30">
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {selections.slice(0, 5).map((selection, idx) => (
                  <div key={selection.id || idx} className="flex items-center gap-1.5 text-sm text-foreground">
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    <span className="truncate max-w-[140px] md:max-w-[180px]">
                      {selection.service_name || selection.experience_name || selection.yacht_name || 'Service'}
                    </span>
                  </div>
                ))}
                {selections.length > 5 && (
                  <span className="text-xs text-muted-foreground self-center">
                    +{selections.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Navigation Row */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                data-testid="back-nav-btn"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">{isFirstTab() ? "Home" : "Back"}</span>
              </button>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">My Plan:</span> <span className="font-medium text-foreground">{selections.length}</span> {selections.length === 1 ? "item" : "items"} selected
              </p>
            </div>
            <button
              onClick={handleContinue}
              data-testid="continue-btn"
              className="px-5 py-2.5 text-white rounded-full text-sm font-medium hover:opacity-90 transition-colors shadow-sm"
              style={{ backgroundColor: 'var(--agent-primary-color)' }}
            >
              {activeCategory === "upgrades" ? (selections.length > 0 ? "Review My Plan" : "View Your Plan") : "Continue"}
            </button>
          </div>
        </div>
      </div>

      {/* Service detail modal */}
      <AnimatePresence>
        {selectedService && (
          <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
            <DialogContent className="dialog-fullscreen-mobile max-w-md max-h-[90vh] flex flex-col p-0 rounded-2xl border-0 shadow-2xl gap-0">
              <div className="flex-1 overflow-y-auto min-h-0">
                <img
                  src={getImageUrl(selectedService.image_url)}
                  alt={selectedService.name}
                  className="w-full aspect-video object-cover"
                  style={{ filter: "brightness(0.97) saturate(0.92)" }}
                />
                <button
                  onClick={() => setSelectedService(null)}
                  className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/95 flex items-center justify-center shadow-md hover:bg-white transition-colors"
                >
                  <X className="w-4 h-4 text-foreground" />
                </button>
              
              {/* Modal content */}
              <div className="p-8">
                {/* Title */}
                <h2 className="font-semibold text-foreground heading-section">
                  {selectedService.name}
                </h2>
                
                <p className="tile-price mt-2 mb-4">
                  {selectedService.price_display}
                </p>
                
                {/* Service modal content - preserve exact formatting as written in CMS */}
                <div className="mt-4">
                  <p className="text-base text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {selectedService.description}
                  </p>
                </div>

                {/* Show stay dates for Car Rental - auto-populated from booking */}
                {selectedService.name === "Car Rental" && booking && (
                  <div className="bg-muted/30 rounded-lg p-4 mb-8">
                    <p className="text-sm font-medium text-foreground mb-2">Rental Period (based on your stay)</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Pick-up:</span>
                        <p className="font-medium">
                          {booking.arrival_date 
                            ? new Date(booking.arrival_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                            : 'Your arrival date'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Return:</span>
                        <p className="font-medium">
                          {booking.departure_date 
                            ? new Date(booking.departure_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                            : 'Your departure date'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show stay dates for Airport Transfers - auto-populated from booking */}
                {selectedService.name === "Airport Transfers" && booking && (
                  <div className="bg-muted/30 rounded-lg p-4 mb-8">
                    <p className="text-sm font-medium text-foreground mb-2">Transfer Dates (based on your stay)</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Arrival Transfer:</span>
                        <p className="font-medium">
                          {booking.arrival_date 
                            ? new Date(booking.arrival_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                            : 'Your arrival date'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Departure Transfer:</span>
                        <p className="font-medium">
                          {booking.departure_date 
                            ? new Date(booking.departure_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                            : 'Your departure date'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedService.questions.length > 0 && (
                  <div className="space-y-7 mb-10 mt-6">
                    {selectedService.questions.map((question) => (
                      <div key={question.id} className="form-group mb-0">
                        <label className="form-label">
                          {question.label}
                          {question.required && <span className="text-destructive ml-1">*</span>}
                        </label>
                        
                        {question.type === "text" && (
                          <input
                            data-testid={`question-${question.id}`}
                            type="text"
                            value={answers[question.id] || ""}
                            onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                            className="form-input"
                          />
                        )}
                        
                        {question.type === "textarea" && (
                          <textarea
                            data-testid={`question-${question.id}`}
                            value={answers[question.id] || ""}
                            onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                            className="form-input resize-none"
                            rows={3}
                          />
                        )}
                        
                        {question.type === "number" && (
                          <input
                            data-testid={`question-${question.id}`}
                            type="number"
                            min="1"
                            value={answers[question.id] || ""}
                            onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                            className="form-input"
                          />
                        )}
                        
                        {question.type === "date" && (
                          <input
                            data-testid={`question-${question.id}`}
                            type="date"
                            value={answers[question.id] || ""}
                            onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                            className="form-input"
                          />
                        )}
                        
                        {question.type === "time" && (
                          <input
                            data-testid={`question-${question.id}`}
                            type="time"
                            value={answers[question.id] || ""}
                            onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                            className="form-input"
                          />
                        )}
                        
                        {question.type === "select" && (
                          <select
                            data-testid={`question-${question.id}`}
                            value={answers[question.id] || ""}
                            onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                            className="form-input bg-background"
                          >
                            <option value="">Select...</option>
                            {question.options?.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        )}
                        
                        {question.type === "boolean" && (
                          <button
                            data-testid={`question-${question.id}`}
                            onClick={() => setAnswers({ ...answers, [question.id]: !answers[question.id] })}
                            className={`w-full py-3 rounded-xl text-sm font-medium transition-colors ${
                              answers[question.id] 
                                ? "bg-primary text-white" 
                                : "bg-secondary text-secondary-foreground"
                            }`}
                          >
                            {answers[question.id] ? "Yes" : "No"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              </div>
              </div>

              {/* Sticky CTA Button */}
              <div className="p-4 border-t border-border/30 bg-background shrink-0">
                <button
                  onClick={handleAddToPlan}
                  data-testid="add-to-plan-btn"
                  disabled={isSaving}
                  className="w-full py-4 rounded-xl font-medium transition-all duration-200 text-white shadow-lg hover:opacity-90"
                  style={{ backgroundColor: 'var(--agent-primary-color)' }}
                >
                  {isSaving ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : isServiceSelected(selectedService.id) ? (
                    "Update selection"
                  ) : (
                    "Add to my plan"
                  )}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>
      
      {/* Dynamic Legal Footer - positioned above the fixed bottom bar */}
      <div className="pb-32">
        <GuestPortalFooter 
          poweredByBrand={agentContext?.powered_by_brand || booking?.powered_by_brand || "my_concierge_portal"}
          privacyPolicyUrl={agentContext?.privacy_policy_url || booking?.privacy_policy_url || "https://myworldvip.com/privacy-policy"}
          termsConditionsUrl={agentContext?.terms_conditions_url || booking?.terms_conditions_url || "https://myworldvip.com/terms-and-conditions"}
        />
      </div>
    </div>
  );
}
